/** Shared clip timing rules. Clip `bars` is always its source/native length. */
import { isRenderedClip } from './clipKinds';
export const BPM_TOLERANCE = 2;

export function isValidBpm(value) { return Number.isFinite(Number(value)) && Number(value) > 0; }

/** Same groove: equal, or half/double tempo (Russell's 70 = 140 convention). */
export function bpmsCompatible(sourceBpm, sessionBpm) {
  const source = Number(sourceBpm); const session = Number(sessionBpm);
  return Math.abs(source - session) <= BPM_TOLERANCE
    || Math.abs(source * 2 - session) <= BPM_TOLERANCE
    || Math.abs(source - session * 2) <= BPM_TOLERANCE;
}

/**
 * Library loops at a tempo that does not fit the song are time-stretched to the song tempo
 * (Signalsmith Stretch, rendered offline). Turn it off per clip with `stretch: false`.
 */
export function needsStretch(clip, sessionBpm) {
  if (!clip || isRenderedClip(clip) || clip.stretch === false || !isValidBpm(clip.sourceBpm) || !isValidBpm(sessionBpm)) return false;
  return !bpmsCompatible(clip.sourceBpm, sessionBpm);
}

/** Half time, normal, double time. Library loops only; drum and keys patterns already follow the song. */
export const SPEED_CHOICES = [0.5, 1, 2];
export function clipSpeed(clip) {
  if (!clip || isRenderedClip(clip)) return 1;
  const speed = Number(clip.speed);
  return SPEED_CHOICES.includes(speed) ? speed : 1;
}

/**
 * Playback-rate factor applied to the source audio: (song BPM / loop BPM when stretched, else 1)
 * times the clip's speed. 0.5 = half time (twice as long), 2 = double time.
 */
export function stretchRate(clip, sessionBpm) {
  const tempo = needsStretch(clip, sessionBpm) ? Number(sessionBpm) / Number(clip.sourceBpm) : 1;
  return tempo * clipSpeed(clip);
}

/** The tempo the clip's untouched audio file was made at (Knock and pattern clips render at the song tempo). */
export function nativeBpm(clip, sessionBpm) {
  if (isRenderedClip(clip)) return Number(sessionBpm);
  return isValidBpm(clip?.sourceBpm) ? Number(clip.sourceBpm) : Number(sessionBpm);
}

/** Loop length in native bars, measured from the original (unstretched) audio buffer. */
export function bufferNativeBars(clip, buffer, sessionBpm) {
  return buffer.duration / (240 / nativeBpm(clip, sessionBpm));
}

// Legacy clips without source metadata intentionally remain session-relative.
// A stretched loop runs at the song tempo, so it counts as session-relative too; speed scales it.
export function effectiveSourceBpm(clip, sessionBpm) {
  if (isRenderedClip(clip)) return Number(sessionBpm);
  const base = needsStretch(clip, sessionBpm) ? Number(sessionBpm) : nativeBpm(clip, sessionBpm);
  return base * clipSpeed(clip);
}

export function timelineBars(clip, sessionBpm) {
  return Number(clip?.bars || 0) * Number(sessionBpm) / effectiveSourceBpm(clip, sessionBpm);
}

export function isTempoMatched(clip, sessionBpm) {
  return bpmsCompatible(effectiveSourceBpm(clip, sessionBpm), sessionBpm);
}

export function clipEndBar(clip, sessionBpm) { return Number(clip.start_bar || 0) + timelineBars(clip, sessionBpm); }
export function sessionBarsToNative(sessionBars, clip, sessionBpm) { return Number(sessionBars) * effectiveSourceBpm(clip, sessionBpm) / Number(sessionBpm); }

export function snapClipStart(clip, requestedBar, sessionBpm, snapToGrid) {
  const gridBar = snapToGrid(requestedBar);
  if (clip.isWholeLoop && isTempoMatched(clip, sessionBpm)) {
    const length = timelineBars(clip, sessionBpm);
    if (length > 0) return Math.round(gridBar / length) * length;
  }
  return gridBar;
}
